import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen, Clock, Film, GraduationCap, Mic, PenLine, PlayCircle, Sparkles, Headphones } from "lucide-react";
import { Button } from "@/components/ui/button";
import BilingualText from "@/components/BilingualText";
import Mascot from "@/components/Mascot";
import { MASCOT } from "@/lib/mascot";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { t } from "@/lib/i18n";
import { IDS } from "@/constants/testIds";

const skills = [
  { icon: BookOpen, cn: "阅读理解", en: "Reading", color: "bg-sky-100 text-sky-700" },
  { icon: Sparkles, cn: "词汇 & 语法", en: "Vocab & Grammar", color: "bg-emerald-100 text-emerald-700" },
  { icon: Headphones, cn: "听力", en: "Listening", color: "bg-amber-100 text-amber-700" },
  { icon: PenLine, cn: "写作", en: "Writing", color: "bg-fuchsia-100 text-fuchsia-700" },
  { icon: Mic, cn: "口语", en: "Speaking", color: "bg-rose-100 text-rose-700" },
];

export default function Landing() {
  const nav = useNavigate();
  const { startGuest } = useAuth();
  const [lessons, setLessons] = useState([]);

  useEffect(() => {
    api.get("/lessons").then(({ data }) => setLessons(data || [])).catch(() => {});
  }, []);

  const startCategory = (cat) => {
    startGuest(cat);
    nav("/dashboard");
  };

  const openLesson = (slug) => {
    // Guest access: prime guest state so /lessons/:slug route (Protected) passes
    if (!localStorage.getItem("aeis_guest") && !localStorage.getItem("aeis_token")) {
      startGuest("10-12");
    }
    nav(`/lessons/${slug}`);
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="mx-auto max-w-7xl px-4 sm:px-8 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-2xl bg-gradient-to-br from-sky-400 to-emerald-500 grid place-items-center shadow-md">
            <GraduationCap className="w-5 h-5 text-white" />
          </span>
          <BilingualText cn={t.brand.cn} en={t.brand.en} size="sm" />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            className="rounded-full font-bold"
            onClick={() => nav("/auth?mode=login")}
            data-testid={IDS.landing.signIn}
          >
            <span className="cn mr-1">登录</span>Sign in
          </Button>
          <Button
            className="rounded-full font-bold bg-slate-900 hover:bg-slate-800 text-white"
            onClick={() => nav("/auth?mode=register")}
            data-testid={IDS.landing.signUp}
          >
            <span className="cn mr-1">注册</span>Sign up
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section
        data-testid={IDS.landing.hero}
        className="mx-auto max-w-7xl px-4 sm:px-8 pt-6 sm:pt-16 pb-24 grid md:grid-cols-12 gap-12 items-center"
      >
        <div className="md:col-span-7 space-y-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 px-4 py-1.5 text-xs font-bold text-amber-800"
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span className="cn">中文在上 · 英文在下 · AI 一对一辅导</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05, duration: 0.6 }}
            className="serif text-4xl sm:text-6xl font-black tracking-tight leading-[1.05]"
          >
            <span className="cn block text-slate-900">
              准备好走进新加坡了吗？
            </span>
            <span className="block mt-1 text-slate-700">
              Your bilingual road to <span className="text-sky-600">AEIS</span>.
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="text-lg text-slate-600 max-w-xl leading-relaxed"
          >
            <span className="cn block text-slate-800 mb-1">
              为 7–12 岁的孩子量身设计的剑桥 A2 / B1 双语备考。听说读写全练，AI 即时纠错。
            </span>
            Cambridge A2 & B1 practice made bilingual — every question in Chinese first,
            English second. AI feedback on writing and speaking. No signup needed to start.
          </motion.p>

          {/* Category picker */}
          <div className="grid sm:grid-cols-2 gap-4 pt-2">
            <motion.button
              onClick={() => startCategory("7-9")}
              data-testid={IDS.landing.ageA2}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              whileHover={{ y: -3 }}
              className="text-left card-plush p-6 hover:border-sky-300 transition-colors"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="badge-soft bg-sky-100 text-sky-700">A2 · Key</span>
                <ArrowRight className="w-5 h-5 text-slate-400" />
              </div>
              <BilingualText cn={t.cat_a2.cn} en={t.cat_a2.en} size="lg" />
              <p className="mt-2 text-sm text-slate-500">
                <span className="cn">生动有趣，配以插图和大字体。</span>
                <span className="block">Playful, illustrated, big-font practice.</span>
              </p>
            </motion.button>

            <motion.button
              onClick={() => startCategory("10-12")}
              data-testid={IDS.landing.ageB1}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.28, duration: 0.5 }}
              whileHover={{ y: -3 }}
              className="text-left card-plush p-6 hover:border-emerald-300 transition-colors"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="badge-soft bg-emerald-100 text-emerald-700">
                  B1 · Preliminary
                </span>
                <ArrowRight className="w-5 h-5 text-slate-400" />
              </div>
              <BilingualText cn={t.cat_b1.cn} en={t.cat_b1.en} size="lg" />
              <p className="mt-2 text-sm text-slate-500">
                <span className="cn">聚焦考试，图表清晰，进度可视化。</span>
                <span className="block">Exam-focused, data-rich progress tracking.</span>
              </p>
            </motion.button>
          </div>

          <p className="text-xs text-slate-400 pt-2">
            <span className="cn">不需要注册 · 数据本地保存</span> · No sign-up needed
            — progress saves locally.
          </p>
        </div>

        {/* Right column: skill mosaic */}
        <div className="md:col-span-5">
          {/* Mascot spotlight */}
          <div className="relative flex justify-center mb-6">
            <div className="absolute inset-x-0 top-8 h-40 rounded-full bg-gradient-to-br from-amber-100 via-emerald-100 to-sky-100 blur-2xl opacity-70" />
            <motion.div
              initial={{ opacity: 0, y: 20, rotate: -3 }}
              animate={{ opacity: 1, y: 0, rotate: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="relative"
            >
              <Mascot size="hero" />
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 }}
                className="absolute -bottom-3 left-1/2 -translate-x-1/2 whitespace-nowrap card-plush px-4 py-2 flex items-center gap-2 shadow-lg"
              >
                <span className="cn text-sm font-black text-slate-900">{MASCOT.cn}</span>
                <span className="text-xs font-bold text-slate-400">·</span>
                <span className="text-sm font-bold text-slate-700">{MASCOT.en}</span>
              </motion.div>
            </motion.div>
          </div>

          <div className="dot-grid rounded-3xl p-6 sm:p-8 border border-slate-100 bg-white/60 backdrop-blur">
            <BilingualText
              cn="覆盖 CEQ 全部五项技能"
              en="All 5 CEQ skills covered"
              size="lg"
            />
            <div className="mt-6 grid grid-cols-2 gap-3">
              {skills.map((s, i) => (
                <motion.div
                  key={s.en}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.06 }}
                  className="card-plush p-4 flex items-start gap-3"
                >
                  <span className={`w-9 h-9 rounded-2xl grid place-items-center ${s.color}`}>
                    <s.icon className="w-4 h-4" />
                  </span>
                  <BilingualText cn={s.cn} en={s.en} size="sm" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Video lessons panel */}
      {lessons.length > 0 && (
        <section
          data-testid="landing-lessons-panel"
          className="mx-auto max-w-7xl px-4 sm:px-8 pb-24"
        >
          <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-4 py-1.5 text-xs font-bold text-emerald-800 mb-3">
                <Film className="w-3.5 h-3.5" />
                <span className="cn">小甲教授在线课</span>
                <span>· with Prof. Scaly</span>
              </div>
              <h2 className="serif text-3xl sm:text-4xl font-black text-slate-900 leading-tight">
                <span className="cn block">看视频，学英文。</span>
                <span>Watch. Listen. Learn.</span>
              </h2>
              <p className="mt-3 text-slate-500 max-w-xl">
                <span className="cn block text-slate-700">
                  小甲教授站在黑板前，一步步教你英语的基础知识。中英双语字幕。
                </span>
                Prof. Scaly teaches at the blackboard, step by step. Bilingual captions in every lesson.
              </p>
            </div>
            <Button
              variant="outline"
              className="rounded-full font-bold"
              onClick={() => {
                if (!localStorage.getItem("aeis_guest") && !localStorage.getItem("aeis_token")) {
                  startGuest("10-12");
                }
                nav("/lessons");
              }}
              data-testid="landing-see-all-lessons"
            >
              <span className="cn mr-1">全部课程</span>All lessons
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {lessons.map((l, i) => (
              <motion.button
                key={l.slug}
                type="button"
                onClick={() => openLesson(l.slug)}
                data-testid={`landing-lesson-${l.slug}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06, duration: 0.5 }}
                whileHover={{ y: -4 }}
                className="text-left card-plush overflow-hidden group hover:shadow-md transition-shadow"
              >
                <div className="relative aspect-video bg-gradient-to-br from-emerald-100 via-amber-50 to-sky-100 grid place-items-center overflow-hidden">
                  <span className="absolute top-3 left-3 badge-soft bg-white/90 backdrop-blur">
                    {l.level}
                    {l.grade_hint ? ` · ${l.grade_hint}` : ""}
                  </span>
                  {l.has_video && (
                    <span className="absolute top-3 right-3 badge-soft bg-rose-500 text-white shadow">
                      <Film className="w-3 h-3" /> HD Video
                    </span>
                  )}
                  <Mascot size="md" ring={false} className="rounded-2xl border-4 border-white shadow-lg" />
                  <span className="absolute bottom-3 right-3 w-12 h-12 rounded-full bg-slate-900 text-white grid place-items-center shadow-xl group-hover:scale-110 transition-transform">
                    <PlayCircle className="w-6 h-6" />
                  </span>
                </div>
                <div className="p-5">
                  <BilingualText cn={l.title_cn} en={l.title_en} size="md" />
                  <p className="mt-2 text-sm text-slate-500 leading-snug line-clamp-2">
                    <span className="cn block text-slate-700">{l.description_cn}</span>
                    {l.description_en}
                  </p>
                  <div className="mt-3 text-xs text-slate-400 font-bold inline-flex items-center gap-1.5">
                    <Clock className="w-3 h-3" />
                    ~{Math.max(1, Math.round((l.duration_est || 240) / 60))} min · {l.slide_count} slides
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
